/**
 * The guided tour — Fluent v9 rendition of references/ui-spec.md §3.
 *
 * One fixed box is the spotlight (a huge box-shadow is the scrim, and it blocks the page) and one
 * fixed card explains the step. Steps arrive already filtered to what is on screen — or, when a
 * dialog is open, to that dialog (`scope`).
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@fluentui/react-components';
import {
  ChevronDown12Regular,
  CursorClick20Regular,
  Dismiss16Regular,
  Next16Regular,
  Previous16Regular,
} from '@fluentui/react-icons';
import { findStepElement, HELP_PANEL_ATTR } from './dom';
import { HelpStepJump } from './HelpStepJump';
import { cardPosition, rectOf, type Rect } from './position';
import { fmt, HELP_STRINGS } from './strings';
import { HELP_TABSTER_ATTRS } from './tabster';
import { groupStepsBySection, localizedSection, localizedStep, type HelpLanguage, type HelpStep, type PageHelp } from './types';

const CARD_WIDTH = 360;
/** Estimated card height for the flip-above decision. */
const CARD_HEIGHT = 200;
const PAD = 4;
/** Smooth scroll lies for a while: track the target rect for this many frames. */
const SETTLE_FRAMES = 40;

interface HelpTourProps {
  help: PageHelp;
  /** Already role- and on-screen-filtered. */
  steps: HelpStep[];
  /** The open dialog the tour is scoped to, or null for the page. */
  scope: HTMLElement | null;
  startIndex: number;
  language: HelpLanguage;
  onInspect: () => void;
  onClose: () => void;
}

export const HelpTour = ({ help, steps, scope, startIndex, language, onInspect, onClose }: HelpTourProps) => {
  const s = HELP_STRINGS[language];
  const [index, setIndex] = useState(Math.max(0, startIndex));
  const [rect, setRect] = useState<Rect | null>(null);
  const [jumpOpen, setJumpOpen] = useState(false);
  const counterRef = useRef<HTMLButtonElement>(null);
  const elementRef = useRef<HTMLElement | null>(null);

  const step = steps[index];
  const groups = useMemo(() => groupStepsBySection(help.sections, steps), [help.sections, steps]);
  const groupIndex = groups.findIndex((g) => g.steps.includes(step));

  const goTo = useCallback(
    (i: number) => {
      if (i < 0) return;
      if (i >= steps.length) onClose();
      else setIndex(i);
    },
    [steps.length, onClose],
  );

  // Find the element; skip a step that vanished (this also ends a tour whose dialog closed).
  useLayoutEffect(() => {
    if (!step) return undefined;
    const root = scope && scope.isConnected ? scope : document;
    const el = findStepElement(step.selector, root);
    if (!el) {
      elementRef.current = null;
      goTo(index + 1);
      return undefined;
    }
    elementRef.current = el;
    el.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    setRect(rectOf(el));

    let frames = 0;
    let raf = 0;
    const track = () => {
      if (elementRef.current) setRect(rectOf(elementRef.current));
      frames += 1;
      if (frames < SETTLE_FRAMES) raf = requestAnimationFrame(track);
    };
    raf = requestAnimationFrame(track);
    const remeasure = () => {
      if (elementRef.current) setRect(rectOf(elementRef.current));
    };
    window.addEventListener('resize', remeasure);
    window.addEventListener('scroll', remeasure, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', remeasure);
      window.removeEventListener('scroll', remeasure, true);
    };
  }, [step, scope, index, goTo]);

  // Keyboard in the CAPTURE phase, so ending the tour does not also close the dialog beneath.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (jumpOpen) return; // the jump list owns the keyboard while open
      const action =
        e.key === 'Escape' ? onClose : e.key === 'ArrowRight' ? () => goTo(index + 1) : e.key === 'ArrowLeft' ? () => goTo(index - 1) : null;
      if (!action) return;
      e.preventDefault();
      e.stopPropagation();
      action();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [jumpOpen, index, goTo, onClose]);

  if (!step || !rect) return null;

  const shown = localizedStep(step, language);
  const isLast = index === steps.length - 1;
  const pos = cardPosition(rect, { width: CARD_WIDTH, height: CARD_HEIGHT });

  return (
    <>
      <div
        className="pct-help-spotlight"
        style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2 }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className="pct-help-float"
        role="dialog"
        aria-label={fmt(s.tourAria, { n: index + 1, m: steps.length, title: shown.title })}
        {...{ [HELP_PANEL_ATTR]: 'tour' }}
        {...HELP_TABSTER_ATTRS}
        style={{ top: pos.top, left: pos.left, width: CARD_WIDTH }}
      >
        <div className="pct-help-float-row">
          <button
            ref={counterRef}
            type="button"
            className="pct-help-counter"
            aria-haspopup="dialog"
            aria-expanded={jumpOpen}
            onClick={() => setJumpOpen((o) => !o)}
            data-help="help-tour-jump"
          >
            {fmt(s.stepOf, { n: index + 1, m: steps.length })}
            <ChevronDown12Regular />
          </button>
          <span className="pct-help-spacer" />
          {groups.length > 1 && (
            <>
              <button
                type="button"
                className="pct-help-icon-button"
                title={s.prevSection}
                aria-label={s.prevSection}
                disabled={groupIndex <= 0}
                onClick={() => goTo(steps.indexOf(groups[groupIndex - 1].steps[0]))}
              >
                <Previous16Regular />
              </button>
              <button
                type="button"
                className="pct-help-icon-button"
                title={s.nextSection}
                aria-label={s.nextSection}
                disabled={groupIndex >= groups.length - 1}
                onClick={() => goTo(steps.indexOf(groups[groupIndex + 1].steps[0]))}
              >
                <Next16Regular />
              </button>
            </>
          )}
          <button type="button" className="pct-help-icon-button" title={s.endTour} aria-label={s.endTour} onClick={onClose}>
            <Dismiss16Regular />
          </button>
        </div>
        {step.section && <div className="pct-help-float-section">{localizedSection(help, step.section, language)}</div>}
        <div className="pct-help-float-title">
          {shown.title}
          {!shown.translated && (
            <span className="pct-help-pill" title={s.untranslated}>
              EN
            </span>
          )}
        </div>
        <div className="pct-help-float-body">{shown.body}</div>
        <div className="pct-help-float-actions">
          <Button
            appearance="subtle"
            size="small"
            icon={<CursorClick20Regular />}
            onClick={onInspect}
            style={{ fontSize: 11.5 }}
            data-help="help-tour-inspect"
          >
            {s.pointAtField}
          </Button>
          <span className="pct-help-spacer" />
          <Button appearance="transparent" size="small" disabled={index === 0} onClick={() => goTo(index - 1)}>
            {s.back}
          </Button>
          <Button appearance="primary" size="small" onClick={() => goTo(index + 1)}>
            {isLast ? s.done : s.next}
          </Button>
        </div>
      </div>
      {jumpOpen && (
        <HelpStepJump
          anchor={counterRef.current}
          help={help}
          steps={steps}
          currentIndex={index}
          language={language}
          onPick={(i) => {
            setJumpOpen(false);
            goTo(i);
          }}
          onClose={() => setJumpOpen(false)}
        />
      )}
    </>
  );
};
